import { FC, useCallback } from 'react';
import { MasterPassCardType } from 'Models';
import { routePath } from 'Navigator/routes';
import { useHistory } from 'react-router-dom';
import { useLocalization, useModal } from 'Hooks';
import { useCreditCardList } from 'Hooks/useCreditCardList';
import { fontSizes, gutters } from 'Theme/Variables';
import { Box, Typography, Dialog } from '@material-ui/core';
import { CreditCardIconLarge, PlaceholderCardIcon } from 'Components/Svg';
import { ChevronDarkRightIcon, MasterpassLogo, CloseIcon, DarkPlusIcon, DialogCloseComponent } from 'Components';
import { PaymentMethodItem } from 'Views/CardApplicationSteps/PaymentStep/ChoosePaymentMethod/PaymentMethodItem/PaymentMethodItem';
import { SelectedPaymentMethod } from 'Views/CardApplicationSteps/PaymentStep/ChoosePaymentMethod/SelectedPaymentMethod/SelectedPaymentMethod';
import './ChoosePaymentMethod.scss';

type ChoosePaymentMethodProps = {
  selectedPaymentMethod?: MasterPassCardType;
  creditCardList?: MasterPassCardType[];
  onPaymentMethodSelect?: (paymentMethod: MasterPassCardType) => void;
};

const ChoosePaymentMethod: FC<ChoosePaymentMethodProps> = ({
  selectedPaymentMethod,
  creditCardList,
  onPaymentMethodSelect,
}) => {
  const history = useHistory();
  const {
    web_application_payment_type_header,
    web_application_payment_type,
    web_application_payment_type_desc,
    web_application_payment_popup_header,
    web_application_payment_popup_desc,
    web_application_payment_popup_btn,
    web_application_payment_popup_empty_card_text,
    web_application_payment_popup_empty_card_desc,
  } = useLocalization();
  const { isVisible, show: openCardDialog, hide } = useModal();
  const { loadCreditCardList } = useCreditCardList();

  // Kart listesi yalnızca seçim dialog'u açıldığında yükleniyor; sayfa açılışında
  // liste ekranda görünmediği için orada çekmek gereksiz bir istek demekti.
  const show = useCallback(() => {
    loadCreditCardList();
    openCardDialog();
  }, [loadCreditCardList, openCardDialog]);

  const handlePaymentMethodSelect = (paymentMethod: MasterPassCardType) => {
    if (onPaymentMethodSelect) {
      onPaymentMethodSelect(paymentMethod);
    }
    hide();
  };

  return (
    <div id="choosePaymentMethod">
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Typography variant="h6">
          <Box fontWeight="bold" marginBottom={fontSizes.regular}>
            {web_application_payment_type_header}
          </Box>
        </Typography>
        <MasterpassLogo />
      </Box>
      {!selectedPaymentMethod && (
        <Box
          className="choosePaymentMethod__container"
          onClick={() => show()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); show(); } }}
        >
          <Box display="flex" alignItems="center">
            <PlaceholderCardIcon />
            <Box>
              <Typography variant="h6">
                <Box fontWeight="bold" className="choosePaymentMethod__toBeLoaded">
                  {web_application_payment_type}
                </Box>
              </Typography>
              <Typography variant="body1">
                <Box>{web_application_payment_type_desc}</Box>
              </Typography>
            </Box>
          </Box>
          <ChevronDarkRightIcon />
        </Box>
      )}
      {selectedPaymentMethod && (
        <Box
          onClick={show}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); show(); } }}
        >
          <SelectedPaymentMethod
            paymentMethodItem={selectedPaymentMethod}
            hide={() => {}}
            isDialog={false}
            onSelect={handlePaymentMethodSelect}
          />
        </Box>
      )}
      <Dialog open={isVisible} onClose={hide}>
        <div className="dialogContainer choosePaymentMethod__dialog">
          <Box position="absolute" top={-5} right={3}>
            <DialogCloseComponent closeFunction={()=>hide()}/>
          </Box>
          <Box>
            <Typography variant="h3" align={creditCardList && creditCardList.length > 0 ? 'left' : 'center'}>
              <Box fontWeight="bold" fontSize={fontSizes.big} marginBottom={gutters.regular}>
                {web_application_payment_popup_header}
              </Box>
            </Typography>
            <Typography variant="body1" align={creditCardList && creditCardList.length > 0 ? 'left' : 'center'}>
              <Box fontSize={fontSizes.regular} marginBottom={gutters.big}>
                {web_application_payment_popup_desc}
              </Box>
            </Typography>
            <Box className="choosePaymentMethod__dialog__container">
              {creditCardList && creditCardList.length > 0 ? (
                creditCardList.map((item, index) => {
                  return (
                    <PaymentMethodItem
                      key={index}
                      paymentMethodItem={item}
                      isDialog
                      hide={hide}
                      onSelect={handlePaymentMethodSelect}
                      isSelected={
                        (selectedPaymentMethod?.maskedCardNumber &&
                          item?.maskedCardNumber &&
                          selectedPaymentMethod.maskedCardNumber.slice(0, 6) + selectedPaymentMethod.maskedCardNumber.slice(14, 16) ===
                            item.maskedCardNumber.slice(0, 6) + item.maskedCardNumber.slice(14, 16)) ||
                        false
                      }
                    />
                  );
                })
              ) : (
                <Box className="choosePaymentMethod__emptyState">
                  <CreditCardIconLarge width={130} height={130} className="icon" />
                  <Typography variant="h6" align="center">
                    <Box fontWeight="bold" marginBottom={1}>
                      {web_application_payment_popup_empty_card_text}
                    </Box>
                  </Typography>
                  <Typography variant="body1" align="center">
                    <Box color="text.secondary">{web_application_payment_popup_empty_card_desc}</Box>
                  </Typography>
                </Box>
              )}
            </Box>
            <div
              onClick={() => {
                history.push(routePath.addCreditCard, { fromCardApplicationSteps: true });
              }}
              tabIndex={0}
              role="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  history.push(routePath.addCreditCard, { fromCardApplicationSteps: true });
                }
              }}
              itemID="addNewCreditCardToPaymentStepBtnTest"
            >
              <Box className="chooseCard__dialog--addButton">
                <DarkPlusIcon />
                <Box flex={1}>
                  <Typography variant="h6" align="center">
                    <Box fontWeight={600}>{web_application_payment_popup_btn}</Box>
                  </Typography>
                </Box>
              </Box>
            </div>
          </Box>
        </div>
      </Dialog>
    </div>
  );
};

export default ChoosePaymentMethod;
